<template>
  <ViewContainer :title="$t('insights.report_other')">
    <template v-if="isInsightsAdmin" v-slot:header-right>
      <OrganizationSelector
        :selectedOrganizationId="selectedOrganizationId"
        @change="onOrganizationChange"
      />
    </template>

    <Filters
      class="mb-6"
      v-if="filtersByReport && initialValues"
      :currentOrganizationId="activeOrganizationId"
      :key="filtersKey"
      :account="account"
      :reportsList="reportsList"
      :filtersByReport="filtersByReport"
      :initialFilters="initialValues"
      :loading="isLoading"
      @filterSelected="getInsightsWithFilters"
      @showWrongFiltersEmptyState="showWrongFiltersEmptyState = true"
    />

    <EmptyState v-if="showWrongFiltersEmptyState && !isLoading" />

    <LoadingState
      v-else-if="isLoading && !showWrongFiltersEmptyState"
      data-testid="insights_summary_empty_state"
      :isLoading="isLoading"
      :imageSource="emptyImage"
      :title="$t('insights.we_are_getting_data')"
      :subtitle="$t('insights.wait_a_moment_please')"
    />

    <div v-else data-testid="insights_summary_content">
      <Report
        :showLastUpdatedLabel="true"
        :report="report"
        :controls="controlsList"
        :controlsInitialValues="controlsInitialValues"
        :isLoadingExport="isLoadingExport"
        :reportType="reportType"
        :hasExportableType="Boolean(exportableType.length)"
        @onExport="handleExport"
        @onPrint="goToPrintView"
      />
    </div>

    <ReportsDrawer
      v-if="isReportsDrawerOpen"
      :visible="isReportsDrawerOpen"
      :organizationId="activeOrganizationId"
      :isReportInfoAlertShown="isReportInfoAlertShown"
      @close="isReportsDrawerOpen = false"
      @closeAlert="isReportInfoAlertShown = false"
    />

    <ConfirmationModal
      v-if="isRoutinesExportConfirmationShown"
      :cancelText="$t('insights.routines_export_deny_button')"
      :confirmText="$t('insights.routines_export_confirm_button')"
      :persistent="false"
      :show="isRoutinesExportConfirmationShown"
      :text="''"
      :title="$t('insights.routines_export_confirmation')"
      @cancel="handleRoutinesExport({ includeResponses: false })"
      @close="isRoutinesExportConfirmationShown = false"
      @confirm="handleRoutinesExport({ includeResponses: true })"
    />

    <ConfirmationModal
      v-if="isTasksExportConfirmationShown"
      modalTestID="reportsView-modal-tasksExportConfirmation"
      :cancelText="$t('common.button.cancel')"
      :confirmText="$t('common.button.confirm')"
      :persistent="false"
      :show="isTasksExportConfirmationShown"
      :text="$t('insights.tasks_export_confirmation')"
      :title="$t('common.attention')"
      @cancel="isTasksExportConfirmationShown = false"
      @close="isTasksExportConfirmationShown = false"
      @confirm="handleTasksExport"
    />
  </ViewContainer>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import _ from 'lodash'
import mixin from '@/mixins/mixin.vue'
import { utils } from 'frontend-js-shared'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'

import api from '@/api'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import Filters from '@/containers/ReportsView/Filters/Filters.vue'
import LoadingState from '@/components/utils/EmptyPageStateManager.vue'
import emptyImage from '@/assets/images/empty-states/empty-state-users.png'
import Report from '@/containers/ReportsView/Report.vue'
import ReportsDrawer from '@/components/ReportsDrawer/ReportsDrawer.vue'
import ConfirmationModal from '@/components/Modals/ConfirmationModal.vue'
import OrganizationSelector from '@/components/OrganizationSelector.vue'
import EmptyState from './EmptyState.vue'

const DEFAULT_INITIAL_REPORT_TYPE = 'overview'
const SIMILAR_REPORT_ERROR_CODE = 'SRE001'
const MAX_ROUTINES_TO_PRESELECT = 50

export default {
  name: 'ReportsView',

  mixins: [mixin, trackerMixin],

  components: {
    ConfirmationModal,
    EmptyState,
    Filters,
    LoadingState,
    OrganizationSelector,
    Report,
    ReportsDrawer,
    ViewContainer,
  },

  data() {
    return {
      activeFilters: {},
      emptyImage,
      filtersKey: 'default',
      initialValues: null,
      isLoading: true,
      isLoadingExport: false,
      isReportInfoAlertShown: false,
      isReportsDrawerOpen: false,
      isRoutinesExportConfirmationShown: false,
      isTasksExportConfirmationShown: false,
      organizationItems: [],
      report: {},
      reportsByType: null,
      reportsList: [],
      reportType: DEFAULT_INITIAL_REPORT_TYPE,
      selectedOrganizationId: '',
      showRevDrawer: false,
      showWrongFiltersEmptyState: false,
      useNewImplementation: false,
    }
  },

  async mounted() {
    const routeQueryOrganization = this.getRouteQuery()?.organization ?? null
    this.selectedOrganizationId = routeQueryOrganization ?? this.organizationId
    this.reportsList = await this.getReportsList(this.organizationId)
    this.reportsByType = await this.getReportsByType(this.reportsList)
    const reportShownFirst = await this.reportsList.find(
      report => report.isShownFirst
    )
    this.initialValues = await this.getQueryValues(reportShownFirst.type)
    this.reportType = this.initialValues.type
    if (this.isInsightsAdmin)
      this.organizationItems = await this.getOrganizations()
  },

  watch: {
    activeControls: {
      deep: true,
      handler(newValue, oldValue) {
        if (!_.isEqual(newValue, oldValue)) {
          this.getInsightsWithControls()
        }
      },
    },

    async accountLanguage() {
      this.reportsList = await this.getReportsList(this.organizationId)
      this.getInsightsWithControls()
    },

    useNewImplementation() {
      this.getInsightsWithControls()
    },

    async report(newReport, oldReport) {
      await this.$nextTick()

      if (this.reportType === 'routines') {
        this.updateInitialFiltersForRoutinesReport(newReport, oldReport)
      }
    },
  },

  computed: {
    ...mapState('report', {
      activeReport: state => state.activeReport,
      activeControls: state => state.activeControls,
    }),

    ...mapGetters(['account', 'organizationId']),

    isInsightsAdmin() {
      return this.account.isInsightsAdmin
    },

    activeOrganizationId() {
      return this.selectedOrganizationId || this.organizationId
    },

    filtersByReport() {
      if (!this.reportsByType) return null
      const filtersByReportType = _.mapValues(
        this.reportsByType,
        report => report.filters
      )

      return filtersByReportType
    },

    controlsList() {
      if (!this.reportsByType) return null
      return this.reportsByType[this.reportType]?.controls ?? []
    },

    controlsInitialValues() {
      if (_.isEmpty(this.controlsList)) return {}
      return this.getControlsInitialValues(this.controlsList)
    },

    exportableType() {
      if (!this.reportsByType) return null
      return this.reportsByType[this.reportType]?.exportableType ?? ''
    },

    accountLanguage() {
      return this.account.language
    },
  },

  methods: {
    ...mapActions({
      getReport: 'report/getReport',
      getReportsList: 'report/getReportsList',
      getAllOrganizations: 'report/getAllOrganizations',
      generateReport: 'report/generateReport',
      downloadReport: 'report/downloadReport',
    }),

    ...mapMutations({
      updateActiveControls: 'report/updateActiveControls',
      updateActiveFilters: 'report/updateActiveFilters',
    }),

    getReportsByType(reportsList) {
      return _.keyBy(reportsList, 'type')
    },

    getControlsInitialValues(controls) {
      if (!_.isEmpty(this.initialValues.controls)) {
        return this.initialValues.controls
      }
      const controlsDefaultValues = {}
      controls.forEach(c => {
        controlsDefaultValues[c.key] = c.default
      })
      return controlsDefaultValues
    },

    getQueryValues(defaultType) {
      const routeQuery = this.getRouteQuery()
      const reportType = routeQuery?.type ?? defaultType

      const initialValues = {
        type: reportType,
        dates: {
          startDate: routeQuery?.startDate ?? '',
          endDate: routeQuery?.endDate ?? '',
        },
        filters: this.getOnlyFilters(routeQuery, reportType),
        controls: this.getOnlyControls(routeQuery, reportType),
      }
      return initialValues
    },

    getOnlyFilters(query, reportType) {
      const filtersByReport = this.reportsByType[reportType]?.filters ?? []

      const matchedFilters = {}
      filtersByReport.forEach(filter => {
        if (filter === 'date') return
        //this is temporal, waiting for backend to send the right values

        if (filter === 'siteAttributes') matchedFilters.tags = query.tags
        if (filter === 'countries')
          matchedFilters.countryCodes = query.countryCodes

        if (_.has(query, filter)) matchedFilters[filter] = query[filter]
      })

      Object.keys(matchedFilters).forEach(key =>
        matchedFilters[key] === undefined ? delete matchedFilters[key] : {}
      )

      return matchedFilters
    },

    getOnlyControls(query, reportType) {
      const controlsByReport = this.reportsByType[reportType]?.controls ?? []
      const matchedControls = {}
      controlsByReport.forEach(c => {
        if (_.has(query, c.key)) matchedControls[c.key] = query[c.key]
      })

      return matchedControls
    },

    async getInsightsReport(type, appliedFilters = {}, appliedControls = {}) {
      try {
        this.isLoading = true
        this.report = await this.getReport({
          organizationId: this.organizationId,
          options: {
            controls: appliedControls,
            endDate: utils.dates.getEndOfDay(appliedFilters.endDate),
            filters: _.omit(appliedFilters, 'startDate', 'endDate'),
            language: this.$store.getters.language ?? 'en',
            locale: window.navigator.language ?? 'en',
            organizationId: this.activeOrganizationId,
            startDate: utils.dates.getStartOfDay(appliedFilters.startDate),
            type: type || this.reportType,
          },
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    getNewControls(type) {
      const newControls = this.reportsByType[type]?.controls
      if (!newControls) return {}

      const controls = {}
      newControls.forEach(control => {
        controls[control.key] = control.default
      })
      return controls
    },

    async getInsightsWithFilters(type, filters) {
      const shouldGetNewControls = this.reportType !== type

      this.reportType = type
      this.showWrongFiltersEmptyState = false
      this.updateActiveFilters(filters)
      this.activeFilters = { ...filters }

      const controls = shouldGetNewControls
        ? await this.getNewControls(type)
        : this.activeControls

      this.setURLQuery()
      this.getInsightsReport(type, filters, controls)
    },

    getInsightsWithControls() {
      const type = this.reportType
      this.getInsightsReport(type, this.activeFilters, this.activeControls)
      this.setURLQuery()
    },

    getCleanFilters() {
      const filters = _.omitBy(this.activeFilters, filter => filter === null)

      const arrayFilters = _.pickBy(filters, filter => _.isArray(filter))

      Object.keys(arrayFilters).forEach(
        key => (arrayFilters[key] = _.compact(arrayFilters[key]))
      )

      return { ...filters, ...arrayFilters }
    },

    handleExport() {
      switch (this.exportableType) {
        case 'routines':
          this.isRoutinesExportConfirmationShown = true
          return
        case 'tasks':
          this.isTasksExportConfirmationShown = true
          return
        default:
          this.generateXLSXReport()
          return
      }
    },

    handleRoutinesExport({ includeResponses }) {
      this.isRoutinesExportConfirmationShown = false

      this.generateXLSXReport({
        controls: {
          includeResponses,
        },
      })
    },

    handleTasksExport() {
      this.isTasksExportConfirmationShown = false
      this.generateXLSXReport()
    },

    async generateXLSXReport(additionalOptions = {}) {
      this.isLoadingExport = true

      const filters = this.getCleanFilters()
      const startDateWithTime = utils.dates.getStartOfDay(filters.startDate)
      const endDateWithTime = utils.dates.getEndOfDay(filters.endDate)
      const { controls: additionalControls = {} } = additionalOptions

      try {
        await api.report.generateReport({
          type: this.exportableType,
          organizationId: this.activeOrganizationId,
          startDate: startDateWithTime,
          endDate: endDateWithTime,
          filters: {
            ...filters,
            startDate: startDateWithTime,
            endDate: endDateWithTime,
          },
          controls: { ...this.activeControls, ...additionalControls },
          language: this.$store.getters.language ?? 'en',
          locale: window.navigator.language ?? 'en',
        })

        this.isReportsDrawerOpen = true
        this.isReportInfoAlertShown = true
        this.trackGenerateReport({
          reportType: this.exportableType,
          filters,
          startDate: startDateWithTime,
          endDate: endDateWithTime,
        })
      } catch (err) {
        if (err.response.data.errorCode === SIMILAR_REPORT_ERROR_CODE) {
          return this.showWarning(
            this.$t('insights.report_with_similar_criteria')
          )
        }
        return this.handleRequestError(err)
      } finally {
        this.isLoadingExport = false
      }
    },

    trackEvent(event, properties = {}) {
      this.$track(`insights.${event}`, this.$store, {
        reportType: this.activeReport,
        ...properties,
      })
    },

    goToPrintView() {
      this.trackEvent('Print')
      this.navigateTo({
        name: 'insightsPrint',
        query: {
          type: this.reportType,
          ...this.activeFilters,
          organization: this.activeOrganizationId,
        },
      })
    },

    async setURLQuery() {
      await this.$router
        .replace({
          name: 'reports',
          query: {
            organization: this.activeOrganizationId,
            type: this.reportType,
            ...this.activeFilters,
            ...this.activeControls,
          },
        })
        .catch(() => {}) // When updating query params a "NavigationDuplicated" exception is thrown. This catch block is to prevent this, but it also could lead to some different exceptions being ignored.
    },

    async getOrganizations() {
      const options = {
        disablePagination: true,
      }
      const response = await this.getAllOrganizations(options)
      return response.results.map(organization => {
        return {
          value: organization._id,
          text: organization.name,
        }
      })
    },

    async onOrganizationChange(organizationId) {
      this.selectedOrganizationId = organizationId
      this.reportsList = await this.getReportsList(this.activeOrganizationId)
      this.filtersKey = this.activeOrganizationId
      this.initialValues = await this.getQueryValues(this.reportType)
    },

    updateInitialFiltersForRoutinesReport(newReport, oldReport) {
      const previouslySelectedRoutines = this.getPreviouslySelectedRoutines(
        oldReport
      )

      // This update is only needed if the user did not select any routines when requesting the report.
      if (!_.isEmpty(previouslySelectedRoutines)) return

      const routineDefinitions = newReport?.metadata?.routineDefinitions ?? []

      // Pre-selecting an arbitrary number of Routines could result in UX issues and requests URI exceeding the maximum length allowed by the web server
      if (routineDefinitions.length > MAX_ROUTINES_TO_PRESELECT) {
        return
      }

      // If user did not select any routine, we update the "routines" filter based on the "routineDefinitions" returned in the report's metadata. That way, we can pre-select these routines in the Autocomplete filter and the user can easily see which routines had executions in the selected date range.
      this.initialValues = {
        ...this.initialValues,
        type: this.reportType, // Should be passed explicitly to avoid an old value due to race conditions
        filters: {
          ...this.initialValues.filters,
          routines: routineDefinitions.map(routine => routine.id),
        },
      }
    },

    getPreviouslySelectedRoutines(prevReportData) {
      const reportRoutines = prevReportData?.metadata?.routineDefinitions?.map?.(
        routine => routine.id
      )

      if (!_.isEmpty(reportRoutines)) return reportRoutines

      return this.activeFilters?.routines ?? []
    },
  },
}
</script>
